// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { replaceInChain } from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    chain,
    LoadRealTimeInventory,
    LoadRealTimePrices,
    WaitForPricingAndInventory,
} from "@insite/client-framework/Store/Pages/StaticList/Handlers/LoadWishListIfNeeded";

export const XngLoadRealTimePrices: any = async (props: any) => {
    const { wishList, productInfosByWishListLineId } = props;
    if (
        !wishList.wishListLineCollection?.length ||
        !getSettingsCollection(props.getState()).productSettings.canSeePrices
    ) {
        props.pricingLoaded = true;
        return;
    }

    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: wishList.wishListLineCollection.map((o: any) => ({
                productId: o.productId,
                unitOfMeasure: o.unitOfMeasure,
                qtyOrdered: o.qtyOrdered,
            })),
            onSuccess: realTimePricing => {
                if (!realTimePricing) {
                    props.pricingLoaded = true;
                    return;
                }
                realTimePricing.realTimePricingResults?.forEach(pricing => {
                    for (const wishListLineId in productInfosByWishListLineId) {
                        const productInfo = productInfosByWishListLineId[wishListLineId]!;
                        if (
                            productInfo.productId === pricing.productId &&
                            productInfo.unitOfMeasure === pricing.unitOfMeasure
                        ) {
                            productInfo.pricing = pricing;
                        }
                    }
                });

                props.pricingLoaded = true;
            },
            onError: () => {
                for (const wishListLineId in productInfosByWishListLineId) {
                    productInfosByWishListLineId[wishListLineId]!.failedToLoadPricing = true;
                }
                props.pricingLoaded = true;
            },
            onComplete(realTimePricingProps) {
                if (realTimePricingProps.apiResult) {
                    this.onSuccess?.(realTimePricingProps.apiResult);
                } else if (realTimePricingProps.error) {
                    this.onError?.(realTimePricingProps.error);
                }
            },
        }),
    );

    if (getSettingsCollection(props.getState()).productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => !!props.pricingLoaded);
    }
};

export const XngLoadRealTimeInventory: any = async (props: any) => {
    const { wishList, productInfosByWishListLineId } = props;
    if (!wishList.wishListLineCollection?.length) {
        props.inventoryLoaded = true;
        return;
    }

    const wishListLineIds = Object.keys(productInfosByWishListLineId);
    props.dispatch(
        loadRealTimeInventory({
            productIds: wishListLineIds.map(o => productInfosByWishListLineId[o]!.productId),
            onSuccess: realTimeInventory => {
                if (!realTimeInventory) {
                    for (const wishListLineId in productInfosByWishListLineId) {
                        productInfosByWishListLineId[wishListLineId]!.failedToLoadInventory = true;
                    }
                } else {
                    realTimeInventory?.realTimeInventoryResults?.forEach(inventory => {
                        wishListLineIds.forEach(o => {
                            const productInfo = productInfosByWishListLineId[o]!;
                            if (productInfo.productId === inventory.productId) {
                                productInfo.inventory = inventory;
                            }
                        });
                    });
                }

                props.inventoryLoaded = true;
            },
            onError: () => {
                for (const wishListLineId in productInfosByWishListLineId) {
                    productInfosByWishListLineId[wishListLineId]!.failedToLoadInventory = true;
                }
                props.inventoryLoaded = true;
            },
            onComplete(realTimeInventoryProps) {
                if (realTimeInventoryProps.apiResult) {
                    this.onSuccess?.(realTimeInventoryProps.apiResult);
                } else if (realTimeInventoryProps.error) {
                    this.onError?.(realTimeInventoryProps.error);
                }
            },
        }),
    );
    await waitFor(() => !!props.inventoryLoaded);
};

// to avoid delay on displaying the lines while waiting for inventory when its not available
export const XngWaitForPricingAndInventory = () => {};

replaceInChain(chain, LoadRealTimePrices, XngLoadRealTimePrices);
replaceInChain(chain, LoadRealTimeInventory, XngLoadRealTimeInventory);
replaceInChain(chain, WaitForPricingAndInventory, XngWaitForPricingAndInventory);
